import React from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  ExpandMore,
  Security,
  AutoAwesome,
  Psychology,
  Campaign,
  Groups,
  Inbox,
  IntegrationInstructions,
  Language,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useWhitelabel } from "../context/WhitelabelContext";
import { getInstanceConfig, getLogoMode } from "../utils";

interface HomePageProps {
  mode: string;
}

const HomePage: React.FC<HomePageProps> = ({ mode }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { whitelabelSettings, whitelabelLogos } = useWhitelabel();
  const instanceConfig = getInstanceConfig(mode, whitelabelSettings);

  const getLogoUrl = () => {
    if (mode === "dark" && whitelabelLogos?.horizontal_logo_dark?.url) {
      return whitelabelLogos.horizontal_logo_dark.url;
    }
    if (whitelabelLogos?.horizontal_logo?.url) {
      return whitelabelLogos.horizontal_logo.url;
    }
    return getLogoMode(mode);
  };

  const appName = whitelabelSettings?.title || "GTM AI";

  const features = [
    {
      icon: <Psychology sx={{ fontSize: 40 }} />,
      title: "Access Every Major AI Model",
      description:
        "OpenAI, Anthropic, Gemini, Grok, DeepSeek, Perplexity and more. Save $400+/month vs paying for ChatGPT Plus, Claude Pro, and Gemini separately.",
    },
    {
      icon: <AutoAwesome sx={{ fontSize: 40 }} />,
      title: "AI That Actually Knows Your Business",
      description:
        "Connect your Google Drive, Notion, Confluence, and databases so every AI interaction is informed by your company knowledge. Process up to 1M tokens of context.",
    },
    {
      icon: <Campaign sx={{ fontSize: 40 }} />,
      title: "Create a Week's Worth of Content in 30 min",
      description:
        "Generate blog posts, social content, marketing copy, graphics, reports, and emails 10x faster. Our users publish 80+ pieces of content/month vs. 10-15 before.",
    },
    {
      icon: <Groups sx={{ fontSize: 40 }} />,
      title: "AI Agents That Talk, Text, and Remember",
      description:
        "Deploy intelligent AI agents across Voice, SMS, Website Chat, and Email. Handle support questions, qualify leads, and book appointments 24/7.",
    },
    {
      icon: <Inbox sx={{ fontSize: 40 }} />,
      title: "Find Your Next 1,000 Customers",
      description:
        "Build AI powered prospecting, enrichment, qualification, automated personalization and 1:1 outreach at scale.",
    },
    {
      icon: <IntegrationInstructions sx={{ fontSize: 40 }} />,
      title: "Powerful Workflow Automation",
      description:
        "Build multi-step AI workflows using our n8n integration with 1000+ business integrations including Slack, Discord, Confluence, and more.",
    },
    {
      icon: <Language sx={{ fontSize: 40 }} />,
      title: "Browser Tasks Automation",
      description:
        "Your AI agents can navigate websites, extract data, monitor competitors, and complete complex multi-step workflows automatically. Save 30+ hours/week.",
    },
    {
      icon: <Security sx={{ fontSize: 40 }} />,
      title: "Enterprise-Grade Security",
      description:
        "Data encryption at rest (AES-256), encryption in transit (TLS 1.2+), LLM no training policy, and audit logging. GDPR & CCPA compliant.",
    },
  ];

  const comparisonData = [
    {
      feature: "Multiple AI Models",
      us: true,
      chatgpt: false,
      jasper: false,
      clay: false,
    },
    {
      feature: "Knowledge Base Integration",
      us: true,
      chatgpt: "limited",
      jasper: false,
      clay: "limited",
    },
    {
      feature: "Automation Workflows",
      us: true,
      chatgpt: false,
      jasper: "limited",
      clay: "limited",
    },
    {
      feature: "AI Voice Agents",
      us: true,
      chatgpt: false,
      jasper: false,
      clay: "extra",
    },
    {
      feature: "Lead Generation",
      us: true,
      chatgpt: false,
      jasper: false,
      clay: true,
    },
  ];

  const faqs = [
    {
      question: "Is my data secure?",
      answer:
        "Your data remains highly secure with enterprise-grade security protocols. We use AES-256 encryption at rest, TLS 1.2+ encryption in transit, and never use your data for model training. We're GDPR & CCPA compliant with 99.9% uptime SLA.",
    },
    {
      question: "What if I'm already using ChatGPT/Claude?",
      answer:
        "You can consolidate all your AI tools into one platform, saving $400+/month. Plus you get access to multiple AI models, knowledge base integration, and automation workflows that standalone tools don't offer.",
    },
    {
      question: "How long does setup take?",
      answer:
        "Most teams are up and running in under 30 minutes. Connect your knowledge base, choose your AI models, and start automating. Our onboarding wizard guides you through every step.",
    },
    {
      question: "Can I try before I buy?",
      answer:
        "Yes! We offer a free plan that lets you explore core features with limited credits. No credit card required to get started.",
    },
  ];

  const steps = [
    {
      number: "01",
      title: "Connect Your Knowledge",
      description: "Link Google Drive, Notion, or upload files",
    },
    {
      number: "02",
      title: "Choose Your AI Models",
      description: "Toggle on the models you need (or all of them)",
    },
    {
      number: "03",
      title: "Start Automating",
      description: "Create content, build lists, deploy agents",
    },
  ];

  const renderFeatureStatus = (status: boolean | string) => {
    if (status === true) {
      return <CheckCircle sx={{ color: "success.main" }} />;
    }
    if (status === false) {
      return <Cancel sx={{ color: "error.main" }} />;
    }
    if (status === "limited") {
      return (
        <Typography variant="body2" color="warning.main">
          Limited
        </Typography>
      );
    }
    if (status === "extra") {
      return (
        <Typography variant="body2" color="warning.main">
          Extra cost
        </Typography>
      );
    }
    return <Cancel sx={{ color: "error.main" }} />;
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Navigation Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1100,
          bgcolor: "background.default",
          borderBottom: 1,
          borderColor: "divider",
          py: 2,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box
              component="img"
              src={getLogoUrl()}
              alt={appName}
              sx={{
                height: 40,
                objectFit: "contain",
                cursor: "pointer",
              }}
              onClick={() => navigate("/")}
            />
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              {!isMobile && (
                <>
                  <Button color="inherit" onClick={() => navigate("/pricing")}>
                    Pricing
                  </Button>
                </>
              )}
              <Button variant="outlined" onClick={() => navigate("/login")}>
                Login
              </Button>
              <Button variant="contained" onClick={() => navigate("/signup")}>
                Get Started
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: `linear-gradient(135deg, ${alpha(instanceConfig.primaryColor, 0.05)} 0%, ${alpha(instanceConfig.primaryColor, 0.1)} 100%)`,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "2rem", md: "3rem" },
                  color: instanceConfig.primaryColor,
                }}
              >
                The All-in-One AI Platform for Business Growth
              </Typography>
              <Typography
                variant="h5"
                sx={{ mb: 2, fontWeight: 500, color: "text.primary" }}
              >
                Finally, AI that knows your business.
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 4, maxWidth: 600 }}
              >
                {appName} replaces Clay, Instantly.ai, Blaze, ChatGPT, Claude,
                Jasper, and 8+ other tools with one unified platform that
                connects to your specific business context. Save $400+/month
                while automating content, leads, and customer conversations.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate("/signup")}
                  sx={{ px: 4, py: 1.5 }}
                >
                  Try for Free
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate("/pricing")}
                  sx={{ px: 4, py: 1.5 }}
                >
                  View Pricing
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Box
                  component="img"
                  src={require("../img/hero.jpg")}
                  alt="AI Platform"
                  sx={{
                    width: "100%",
                    maxWidth: 500,
                    height: "auto",
                    borderRadius: 2,
                    boxShadow: theme.shadows[8],
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Problem/Solution Section */}
      <Box sx={{ py: 8, bgcolor: "background.paper" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            textAlign="center"
            gutterBottom
            sx={{ mb: 6 }}
          >
            The Problem with AI Tools Today
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: "100%",
                  bgcolor: alpha(theme.palette.error.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                }}
              >
                <CardContent>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ color: "error.main" }}
                  >
                    The Old Way
                  </Typography>
                  <Box component="ul" sx={{ pl: 2 }}>
                    <Typography component="li" sx={{ mb: 1 }}>
                      Paying $500+/month for ChatGPT, Claude, Jasper, Copy.ai,
                      Intercom AI...
                    </Typography>
                    <Typography component="li" sx={{ mb: 1 }}>
                      AI that doesn't know your business, products, or customers
                    </Typography>
                    <Typography component="li" sx={{ mb: 1 }}>
                      Copy-pasting between 8 different tools
                    </Typography>
                    <Typography component="li">
                      Manual responses to every customer question
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: "100%",
                  bgcolor: alpha(theme.palette.success.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                }}
              >
                <CardContent>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ color: "success.main" }}
                  >
                    The {appName} Way
                  </Typography>
                  <Box component="ul" sx={{ pl: 2 }}>
                    <Typography component="li" sx={{ mb: 1 }}>
                      $99-499/month for all AI models + automation
                    </Typography>
                    <Typography component="li" sx={{ mb: 1 }}>
                      Connected to your knowledge base (Drive, Notion,
                      Confluence)
                    </Typography>
                    <Typography component="li" sx={{ mb: 1 }}>
                      One dashboard for content, leads, support, and workflows
                    </Typography>
                    <Typography component="li">
                      AI agents that handle 70% automatically
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            textAlign="center"
            gutterBottom
            sx={{ mb: 2 }}
          >
            Everything You Need to Run AI at Scale
          </Typography>
          <Typography
            variant="body1"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 6, maxWidth: 700, mx: "auto" }}
          >
            Access every major AI model, connect your knowledge base, and
            automate workflows - all from one platform.
          </Typography>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: theme.shadows[8],
                    },
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        color: instanceConfig.primaryColor,
                        mb: 2,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box sx={{ py: 8, bgcolor: "background.paper" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            textAlign="center"
            gutterBottom
            sx={{ mb: 2 }}
          >
            Get Started in 3 Simple Steps
          </Typography>
          <Typography
            variant="body1"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 6 }}
          >
            Your team saves 40+ hours/week
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {steps.map((step, index) => (
              <Grid item xs={12} sm={4} key={index}>
                <Box textAlign="center">
                  <Typography
                    variant="h2"
                    sx={{
                      color: alpha(instanceConfig.primaryColor, 0.3),
                      fontWeight: 700,
                      mb: 2,
                    }}
                  >
                    {step.number}
                  </Typography>
                  <Typography variant="h5" gutterBottom>
                    {step.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {step.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Box textAlign="center" sx={{ mt: 6 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/signup")}
              sx={{ px: 6, py: 1.5 }}
            >
              Get Started
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Comparison Table */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            textAlign="center"
            gutterBottom
            sx={{ mb: 2 }}
          >
            Why Teams Choose {appName}
          </Typography>
          <Typography
            variant="body1"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 6 }}
          >
            See how we compare to the competition
          </Typography>
          <TableContainer component={Paper} sx={{ overflow: "auto" }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow
                  sx={{ bgcolor: alpha(instanceConfig.primaryColor, 0.1) }}
                >
                  <TableCell sx={{ fontWeight: "bold" }}>Feature</TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    {appName}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    ChatGPT Teams
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    Jasper
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    Clay + Others
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comparisonData.map((row) => (
                  <TableRow key={row.feature} hover>
                    <TableCell>{row.feature}</TableCell>
                    <TableCell align="center">
                      {renderFeatureStatus(row.us)}
                    </TableCell>
                    <TableCell align="center">
                      {renderFeatureStatus(row.chatgpt)}
                    </TableCell>
                    <TableCell align="center">
                      {renderFeatureStatus(row.jasper)}
                    </TableCell>
                    <TableCell align="center">
                      {renderFeatureStatus(row.clay)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow
                  sx={{ bgcolor: alpha(instanceConfig.primaryColor, 0.05) }}
                >
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Monthly Cost
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: "bold", color: "success.main" }}
                  >
                    $99-499
                  </TableCell>
                  <TableCell align="center">$300+ (team)</TableCell>
                  <TableCell align="center">$499+</TableCell>
                  <TableCell align="center">$600+ (combined)</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Box textAlign="center" sx={{ mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/pricing")}
            >
              View Full Pricing
            </Button>
          </Box>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 8, bgcolor: "background.paper" }}>
        <Container maxWidth="md">
          <Typography
            variant="h3"
            textAlign="center"
            gutterBottom
            sx={{ mb: 6 }}
          >
            Your Questions, Answered
          </Typography>
          {faqs.map((faq, index) => (
            <Accordion key={index} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">{faq.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">{faq.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: 8,
          background: `linear-gradient(135deg, ${instanceConfig.primaryColor} 0%, ${alpha(instanceConfig.primaryColor, 0.8)} 100%)`,
          color: "white",
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Typography variant="h3" gutterBottom sx={{ color: "white" }}>
            Ready to Transform Your Business?
          </Typography>
          <Typography
            variant="h6"
            sx={{ mb: 4, opacity: 0.9, color: "white" }}
          >
            Join thousands of teams saving 40+ hours per week with AI automation
          </Typography>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/signup")}
              sx={{
                bgcolor: "white",
                color: instanceConfig.primaryColor,
                "&:hover": {
                  bgcolor: alpha("#ffffff", 0.9),
                },
                px: 4,
                py: 1.5,
              }}
            >
              Start Free Trial
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate("/pricing")}
              sx={{
                borderColor: "white",
                color: "white",
                "&:hover": {
                  borderColor: "white",
                  bgcolor: alpha("#ffffff", 0.1),
                },
                px: 4,
                py: 1.5,
              }}
            >
              View Pricing
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          bgcolor:
            theme.palette.mode === "dark" ? "background.paper" : "grey.100",
          py: 4,
          textAlign: "center",
          mt: "auto",
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <Container>
          <Box sx={{ mb: 2 }}>
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate("/pricing")}
            >
              Pricing
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate("/security")}
            >
              Security
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate("/terms")}
            >
              Terms
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate("/privacy")}
            >
              Privacy
            </Button>
            <Button color="inherit" size="small" onClick={() => navigate("/login")}>
              Login
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary">
            &copy; {new Date().getFullYear()} {appName}. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
